/**
 * ════════════════════════════════════════════════════════════════════════════
 *  ONE DELAY GROUP FOR EVERY TOOLTIP
 * ════════════════════════════════════════════════════════════════════════════
 *
 * A cold tooltip waits for a deliberate rest; once one is open (or closed less
 * than `skipWindowMs` ago) its neighbours open at 0 ms and without animation,
 * and the previous bubble closes in the same tick — so a row of icon buttons
 * can be read by sliding along it, and a pass across it opens nothing.
 *
 * ── THE NUMBERS ─────────────────────────────────────────────────────────────
 *
 *   600 ms  cold open for `label` / `description`. A pointer sweeping a row of
 *           32–44 px icon buttons spends ~150–250 ms on each, so a sweep opens
 *           nothing and a rest does. AntD's own 100 ms is why dense rows
 *           flashed.
 *   300 ms  cold open for `help` (ⓘ, a dotted phrase, a greyed button): the
 *           user pointed at it BECAUSE they want the answer.
 *   400 ms  skip window after the last bubble closed.
 *   150 ms  hover-out grace, so the pointer can cross into the bubble
 *           (WCAG 1.4.13 hoverable). Consumed by the React layer.
 *     0 ms  keyboard focus.
 *   150 ms  warm rest on a LARGE trigger (≥ 120×48 px of area: a calendar
 *           block, a KPI tile). Icon rows still chain at 0 ms; a pass across
 *           the calendar grid no longer opens a bubble on every block crossed.
 *
 * ── "COLD" MEANS A REST, NOT AN ENTRY ───────────────────────────────────────
 *
 * The cold timer starts at `mouseenter`, but a pointer gliding slowly across a
 * 300 px block is not resting on it. While a request is pending, the React
 * layer calls `rearm()` whenever the pointer has moved more than 6 px from
 * where the timer last started, so the bubble opens `openDelayMs` after the
 * pointer actually STOPS. Warm handoffs are untouched (the icon-row
 * slide-along contract), except for the large-trigger rest above.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
 *
 * Escape for an UNPINNED tooltip. `@rc-component/portal` keeps one Esc stack
 * for tooltips, dialogs and dropdowns and only tells the topmost one, so a
 * tooltip inside a Modal closes on the first Esc and the Modal on the second —
 * with the IME composition guard already in place. A listener of ours that
 * stopped propagation would also swallow Escape for every component that has
 * its own handler (the chart readout, the body map, the bell).
 *
 * Pure: no React, no AntD, no DOM access until `attach`. Timers are read from
 * `globalThis` at call time so fake timers work. Closure-local functions, no
 * `this`, so `const { suspend } = group` is safe.
 */
/** A trigger at least this big (area, px²) is "large": 120 × 48. */
export const LARGE_TRIGGER_AREA = 120 * 48;
/** Pointer travel (px) that restarts a pending cold timer: the pointer is moving, not resting. */
export const REST_TOLERANCE_PX = 6;
export const DEFAULT_TOOLTIP_TIMING = Object.freeze({
    openDelayMs: 600,
    helpOpenDelayMs: 300,
    skipWindowMs: 400,
    closeDelayMs: 150,
    warmRestMs: 150,
});
export function createTooltipGroup(timing = DEFAULT_TOOLTIP_TIMING) {
    let active = null;
    let pending = null;
    let cool = null;
    let warmAfterClose = false;
    let suspended = 0;
    const clearPending = () => {
        if (pending) {
            clearTimeout(pending.h);
            pending = null;
        }
    };
    const clearCool = () => {
        if (cool) {
            clearTimeout(cool);
            cool = null;
        }
    };
    const isWarm = () => active !== null || warmAfterClose;
    const commit = (m, instant, by) => {
        clearPending();
        if (active && active.m.id !== m.id) {
            // Handoff: the previous bubble closes in the same tick, so two are
            // never on screen together.
            const prev = active.m;
            active = null;
            prev.hide();
        }
        clearCool();
        warmAfterClose = false;
        active = { m, by };
        m.show(instant);
    };
    const dismissAll = ({ cool: coolNow = true } = {}) => {
        clearPending();
        const prev = active?.m;
        active = null;
        prev?.hide();
        if (coolNow) {
            clearCool();
            warmAfterClose = false;
        }
    };
    const schedule = (m, delay, instant) => {
        pending = { m, delay, instant, h: setTimeout(() => commit(m, instant, 'pointer'), delay) };
    };
    const request = (m, kind, opts = {}) => {
        if (suspended > 0 || active?.m.id === m.id || pending?.m.id === m.id)
            return;
        clearPending();
        if (isWarm()) {
            const rest = opts.large ? (timing.warmRestMs ?? 0) : 0;
            if (rest > 0)
                schedule(m, rest, true);
            else
                commit(m, true, 'pointer');
            return;
        }
        schedule(m, kind === 'help' ? timing.helpOpenDelayMs : timing.openDelayMs, false);
    };
    const rearm = (m) => {
        if (pending?.m.id !== m.id)
            return;
        const { delay, instant } = pending;
        clearPending();
        schedule(m, delay, instant);
    };
    const openNow = (m, by) => {
        if (suspended > 0)
            return;
        if (active?.m.id === m.id) {
            if (by === 'pin')
                active.by = 'pin';
            return;
        }
        commit(m, isWarm(), by);
    };
    const cancel = (m) => {
        if (pending?.m.id === m.id)
            clearPending();
    };
    const release = (m) => {
        if (pending?.m.id === m.id)
            clearPending();
        if (active?.m.id !== m.id)
            return;
        active = null;
        warmAfterClose = true;
        clearCool();
        cool = setTimeout(() => {
            warmAfterClose = false;
            cool = null;
        }, timing.skipWindowMs);
    };
    const pin = (m) => {
        if (active?.m.id === m.id)
            active.by = 'pin';
    };
    const suspend = () => {
        suspended += 1;
        dismissAll();
        let done = false;
        return () => {
            if (!done) {
                done = true;
                suspended -= 1;
            }
        };
    };
    const attached = new WeakMap();
    const attach = (doc) => {
        const existing = attached.get(doc);
        if (existing)
            return existing;
        const win = doc.defaultView;
        const scrolls = (target, trigger) => Boolean(trigger) && (target === doc || (target instanceof Node && target.contains(trigger)));
        const onScroll = (event) => {
            const target = event.target;
            // A pending cold open whose trigger is scrolling away never commits: the
            // browser only re-dispatches boundary events lazily after a scroll, so
            // the timer would otherwise fire mid-scroll and flash a bubble.
            if (pending && scrolls(target, pending.m.triggerEl()))
                clearPending();
            // Keyboard and pinned bubbles stay: Tab scrolls the focused control into
            // view one frame later, and rc-trigger realigns the bubble on scroll.
            if (!active || active.by !== 'pointer')
                return;
            const trigger = active.m.triggerEl();
            if (scrolls(target, trigger)) {
                dismissAll();
            }
        };
        const onBlur = () => dismissAll();
        const onVisibility = () => {
            if (doc.hidden)
                dismissAll();
        };
        const onPointerDown = (event) => {
            if (active?.by !== 'pin')
                return;
            const target = event.target;
            if (!target)
                return;
            if (!active.m.triggerEl()?.contains(target) && !active.m.popupEl()?.contains(target)) {
                dismissAll();
            }
        };
        // Bubble phase, on the window, and only a pinned bubble: never stops
        // propagation, never prevents default, respects IME composition.
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !event.isComposing && active?.by === 'pin') {
                dismissAll({ cool: false });
            }
        };
        doc.addEventListener('scroll', onScroll, { capture: true, passive: true });
        doc.addEventListener('visibilitychange', onVisibility);
        doc.addEventListener('pointerdown', onPointerDown, { capture: true });
        win?.addEventListener('blur', onBlur);
        win?.addEventListener('keydown', onKeyDown);
        const detach = () => {
            doc.removeEventListener('scroll', onScroll, { capture: true });
            doc.removeEventListener('visibilitychange', onVisibility);
            doc.removeEventListener('pointerdown', onPointerDown, { capture: true });
            win?.removeEventListener('blur', onBlur);
            win?.removeEventListener('keydown', onKeyDown);
            attached.delete(doc);
        };
        attached.set(doc, detach);
        return detach;
    };
    return {
        timing,
        request,
        rearm,
        isPending: (m) => pending?.m.id === m.id,
        openNow,
        cancel,
        release,
        pin,
        dismissAll,
        suspend,
        attach,
        isWarm,
        activeId: () => active?.m.id ?? null,
        openedBy: () => active?.by ?? null,
    };
}
//# sourceMappingURL=tooltip-group.js.map